import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface HoShinSulProps {
  selectedBelt: string;
  getBeltColor: (belt: string) => string;
  techniques: number[];
  hasDemo: (aliases: string[]) => boolean;
  onWatchDemo: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

function getTechniqueDemoAliases(technique: number) {
  if (technique < 1 || technique > 12) return [];
  return [`ho_shin_sul_${technique}`];
}

/** Same numbers as the selectable tabs (compact range when consecutive). */
function formatHoShinSulGradingRequirement(techniques: number[]): string {
  if (techniques.length === 0) return "";
  const sorted = [...new Set(techniques)].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const consecutive =
    sorted.length === max - min + 1 && sorted.every((n, i) => n === min + i);
  if (consecutive) {
    return min === max ? String(min) : `${min}-${max}`;
  }
  return sorted.join(", ");
}

function HoShinSulTechniqueContent({
  technique,
  hasDemo,
  onWatchDemo,
}: {
  technique: number;
  hasDemo: (aliases: string[]) => boolean;
  onWatchDemo: (aliases: string[]) => void;
}) {
  const aliases = getTechniqueDemoAliases(technique);

  return (
    <div className="space-y-4">
      {hasDemo(aliases) && (
        <button
          type="button"
          className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
          onClick={() => onWatchDemo(aliases)}
        >
          Watch Demonstration Video
        </button>
      )}
      {technique === 1 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Inward Release - Attack = Cross grab (Old no. 3),
          </div>
          <ol className="list-none space-y-2">
            {[
              "Stay in Natural stance - Turn your hand inwards, palm facing towards them, don’t circle outwards at all, instead roll your thumb over their wrist",
              "Don’t pull your hand upwards (sometimes described as looking at your watch). Take your hand across - it should end up your palm in line with your shoulder, not on your centre line.",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 2 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Wave Release - Attack = Straight Grab
          </div>
          <ol className="list-none space-y-2">
            {[
              "I. Cat stance facing attacking side - live hand, don’t pull them in at all",
              "II. Natural stance - roll your hand underneath & around outside of their hand",
              "III. Push slightly downwards, towards solar plexus, don’t push straight",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 3 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Circle Step Release - Attack = Cross grab
          </div>
          <ol className="list-none space-y-2">
            {[
              "Half step forward (same side as their attacking hand) - live hand, don’t turn palm upwards yet",
              "Rotate back end round 180* into Back Stance - turn palm upwards facing - as you turn",
              "End up facing same way as them, next to their attacking hand",
              "Lean into front leg – keep other arm with live fingers under theirs touching",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 4 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Inward Release - Attack = Straight Grab
          </div>
          <ol className="list-none space-y-2">
            {[
              "I. Stay in Natural stance - Turn your hand downwards, palm facing the floor",
              "II. Step 90* backwards (to the side, opposite leg to attacking arm), sliding your hand out of their grip, motioning your hand in a circular way following your body. End in back stance 90* to them",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 5 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Rising Release - Attack = Both hands grabbing one wrist
          </div>
          <ol className="list-none space-y-2">
            {[
              "Half step forward - live hand, dipping down",
              "Thumb to same shoulder, don’t open your arm out, keep your arm tightly bent",
              "Straighten up & move elbow high (keeping thumb against shoulder)",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 6 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Salute Release - Attack = Both hands grabbing one wrist
          </div>
          <ol className="list-none space-y-2">
            {[
              "I. Trick inwards (move hand across body)",
              "II. Half step towards them (same leg as arm attacked) – bring hand back under and round the outside into a salute (palm towards them)",
              "III. Roll & cut your hand down (turning hand to face inwards)",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 7 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Diagonal Release & Wave Release - Attack = Both wrists grabbed
          </div>
          <ol className="list-none space-y-2">
            {[
              "Cat Stance - live hands, one hand thumb diagonally towards opposite shoulder (inside attackers arms, dropping weight onto same leg) don’t pull them in towards you",
              "Other hand Natural Stance - roll your hand around theirs like no2, Push slightly downwards",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 8 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Attack = Both wrists grabbed
          </div>
          <ol className="list-none space-y-2">
            {[
              "Fake step diagonally forward, when they resist, go back the other way underneath & around the top & push with both open hands first way again – like you are flinging water off fingers",
              "When making the Live Hand (I sometimes call this Ki fingers), we don’t do this slowly. But it feels soft and alive, rather than forced and stiff.",
              "Students practice as a form with breathing/joonbi movements, helping to separate each tech",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 9 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Elbow Lock - Attack = Cross grab (written as right wrist grab) (Old
            no. 4)
          </div>
          <ol className="list-none space-y-2">
            {[
              "Ki fingers, circle wrist clockwise underneath & over theirs until your palm is facing up in cat stance facing them",
              "grab their wrist as you step left leg forward into a horse stance, apply pressure with your left forearm to pressure point behind their elbow. Brace on your left thigh.",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 10 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Wrist Lock - Attack = Straight Grab (right side) (Old no. 6)
          </div>
          <ol className="list-none space-y-2">
            {[
              "Ki fingers, shift into cat stance, keeping the right hand live, dragon mouth under their wrist (upward arc hand).",
              "Your Left hand grabs on top of their attacking hand in dragon mouth (keep under knuckles).",
              "Circle your hand apply the Wrist Lock early, circling towards Dan Jun (right side).",
              "Keep pressure on and their arm straight, try to get them on the floor with pressure going from your belt knot, down their arm into their shoulder on the floor.",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 11 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Wrist Throw - Attack = Cross grab (right side) (Old no. 7)
          </div>
          <ol className="list-none space-y-2">
            {[
              "Place your hand on the back of their attacking hand/wrist, with thumb on back of their hand",
              "Ki fingers on attacked hand, roll thumb over their wrist, add released hand to the back of their hand, so both your thumbs on back of their attacking hand (wrist lock).",
              "Step back with right leg to front stance, circle trapped hand towards belt knot in a wrist lock, keep both thumbs below their knuckles.",
              "Pivot Front Stance keeping the trapped hand locked tight to face 135* to right to take them down",
              "(alternate could be step through not back)",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 12 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Side Takedown - Attack = Lapel grab straight arm in front stance
            (right side attack) (Old no.5)
          </div>
          <ol className="list-none space-y-2">
            {[
              "Shift weight into cat stance (weight on left leg), whilst trapping their hand (or grabbing their elbow & pulling in tight)",
              "Place left hand on attackers chest (high), left leg snakes round their front foot with heel high, sweep their leg by putting your heel down fast, move your right leg into horse stance, pushing your left hand across (not down)",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 13 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Front Takedown - Attack = Lapel grab pulling in with bent arm –
            Right attack
          </div>
          <ol className="list-none space-y-2">
            {[
              "Step left foot forward and use left hand behind their elbow to pull their weight into their right foot, use your right arm over their left shoulder to lean their weight into their right foot.",
              "Snake your right foot behind their right leg, toes to floor, then heel down to takedown.",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 14 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Dragon Stance Release - Attack = Throat grab (left hand attack) (Old
            no.8)
          </div>
          <ol className="list-none space-y-2">
            {[
              "Dragon stance - take your outside elbow over their arm, turning your neck to breathe, don’t step back at this stage, don’t force their arm off, break their grip by using the stance",
              "Step left leg back into Back Stance (slide back) - flow arm down, live hand",
              "Guard with back hand",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 15 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Wrist/Elbow Lock - Attack = Throat grab (right hand attack) (Old
            no.9)
          </div>
          <ol className="list-none space-y-2">
            {[
              "Grab their wrist with your right hand, turn your body to the right, keeping their hand held tight apply Wrist pressure first",
              "Step L forward into horse stance applying pressure to pressure point behind their elbow with your left knifehand or forearm",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : technique === 16 ? (
        <div className="space-y-4 text-gray-700">
          <div className="font-medium text-taekwondo-red">
            Dropping Wrist Throw - Attack = Throat grab (right hand attack) (Old
            no. 10)
          </div>
          <ol className="list-none space-y-2">
            {[
              "Grab attackers wrist with left hand, turn to your left, keep hold of their attacking hand, peeling it outwards, but keeping it close to your body",
              "place your right knifehand on the back of their hand, forcing their wrist to turn the opposite way",
              "Circle your left leg 90 degrees into Eagle Stance (left knee down, if right hand attack) keeping their wrist close into your belt knot.",
            ].map((instruction, index) => (
              <li key={index} className="flex">
                <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
                <span>{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="text-gray-700">
          Detailed instructions for Ho Shin Sul {technique} will be available
          soon.
        </p>
      )}
    </div>
  );
}

function HoShinSulCurriculum({
  techniques,
  hasDemo,
  onWatchDemo,
  beltTabSurfaceClass,
}: {
  techniques: number[];
  hasDemo: (aliases: string[]) => boolean;
  onWatchDemo: (aliases: string[]) => void;
  beltTabSurfaceClass: string;
}) {
  const [active, setActive] = useState(() => String(techniques[0] ?? "1"));

  useEffect(() => {
    setActive(String(techniques[0] ?? "1"));
  }, [techniques]);

  const surface = beltTabSurfaceClass;
  const gradingSummary = formatHoShinSulGradingRequirement(techniques);

  return (
    <div className="space-y-6 text-left">
      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5">
        <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">
          Required for Grading
        </h3>
        <p className="text-gray-800">{gradingSummary}</p>
      </section>

      <Tabs
        value={active}
        onValueChange={setActive}
        className="w-full space-y-4"
      >
        <ScrollableCurriculumTabStrip
          key={techniques.join(",")}
          ariaLabelPrefix="Ho Shin Sul techniques"
        >
          {techniques.map((t) => (
            <TabsTrigger
              key={t}
              value={String(t)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium tabular-nums border border-black/10 transition-shadow",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              {t}
            </TabsTrigger>
          ))}
        </ScrollableCurriculumTabStrip>

        {techniques.map((t) => (
          <TabsContent
            key={t}
            value={String(t)}
            className="mt-0 focus-visible:outline-none"
          >
            <div className="mt-2 rounded-lg bg-gray-50 p-4 md:p-5">
              <HoShinSulTechniqueContent
                technique={t}
                hasDemo={hasDemo}
                onWatchDemo={onWatchDemo}
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export default function HoShinSul({
  techniques,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: HoShinSulProps) {
  if (techniques.length === 0) return null;

  if (presentation === "curriculum") {
    return (
      <HoShinSulCurriculum
        techniques={techniques}
        hasDemo={hasDemo}
        onWatchDemo={onWatchDemo}
        beltTabSurfaceClass={beltTabSurfaceClass}
      />
    );
  }

  return (
    <Accordion type="single" collapsible className="w-full space-y-2">
      {techniques.map((technique) => (
        <AccordionItem
          key={`hoshin-${technique}`}
          value={`hoshin-${technique}`}
        >
          <AccordionTrigger className="rounded-lg bg-gray-50 px-4 py-2 hover:bg-gray-100 hover:no-underline">
            <div className="flex w-full items-center text-left">
              <span className="flex-1">Ho Shin Sul {technique}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-0">
            <div className="mt-2 rounded-lg bg-gray-50 p-4">
              <HoShinSulTechniqueContent
                technique={technique}
                hasDemo={hasDemo}
                onWatchDemo={onWatchDemo}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
